import  { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { designProcessSteps } from '../data';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { Check, ChevronRight, ChevronLeft } from 'lucide-react';

const AnimatedDesignProcess = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [ref, isInView] = useIntersectionObserver<HTMLDivElement>({ threshold: 0.1, once: true });
  const step = designProcessSteps[currentStep];

  const nextStep = () => {
    setCurrentStep((prev) => (prev + 1) % designProcessSteps.length);
  };

  const prevStep = () => {
    setCurrentStep((prev) => (prev - 1 + designProcessSteps.length) % designProcessSteps.length);
  };

  return (
    <div ref={ref} className="py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h2 className="font-serif text-3xl mb-2 section-title">The Design Process</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Our meticulous custom jewelry creation process ensures that your vision becomes reality
        </p>
      </motion.div>

      <div className="max-w-5xl mx-auto px-4">
        {/* Step indicators */}
        <div className="flex justify-center mb-8 md:mb-12">
          {designProcessSteps.map((s, index) => (
            <div key={s.id} className="flex items-center">
              <motion.button
                className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full ${
                  index <= currentStep ? 'bg-gold text-white' : 'bg-gray-200 text-gray-500'
                } transition-colors duration-300`}
                onClick={() => setCurrentStep(index)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {index < currentStep ? (
                  <Check size={14} className="md:w-4 md:h-4" />
                ) : (
                  <span className="text-sm md:text-base">{index + 1}</span>
                )}
              </motion.button>
              {index < designProcessSteps.length - 1 && (
                <div 
                  className={`w-8 md:w-16 h-1 ${
                    index < currentStep ? 'bg-gold' : 'bg-gray-200'
                  } transition-colors duration-300`}
                ></div>
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 items-center"
            >
              <div className="overflow-hidden rounded-lg">
                <motion.img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-48 md:h-80 object-cover"
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.7 }}
                />
              </div>
              <div>
                <motion.h3
                  className="font-serif text-xl md:text-2xl mb-2 md:mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  Step {step.id}: {step.title}
                </motion.h3>
                <motion.p
                  className="text-gray-600 text-sm md:text-base mb-4 md:mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  {step.description}
                </motion.p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-4 md:mt-8">
            <motion.button
              onClick={prevStep}
              className="flex items-center text-gray-600 hover:text-gold transition-colors text-sm md:text-base"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft size={16} className="mr-1 md:w-5 md:h-5" />
              <span>Previous</span>
            </motion.button>
            <motion.button
              onClick={nextStep}
              className="flex items-center text-gray-600 hover:text-gold transition-colors text-sm md:text-base"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Next</span>
              <ChevronRight size={16} className="ml-1 md:w-5 md:h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedDesignProcess;
 